import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Wallet, CircleDollarSign, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import api, { formatApiError } from "../api";
import { queueRequest, isNetworkError } from "../offline";

const KATEGORI = ["Uang Makan Supir", "Solar", "Uang Bongkar Muatan", "Retribusi Jalan", "Uang Ganti Ban/Darurat"];
const fmtRp = (v) => `Rp ${Number(v || 0).toLocaleString("id-ID")}`;
const fmtDate = (v) => {
  const d = new Date(v + "T00:00:00");
  return isNaN(d) ? v : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
};

const emptyExpense = () => ({
  Kategori_Biaya: "",
  Nominal_Rp: "",
  Tanggal_Keluar: new Date().toISOString().slice(0, 10),
  Catatan: "",
});

export const TripExpenses = ({ tripId, statusTrip, komoditas }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(emptyExpense());
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const active = statusTrip !== "Draft";

  const load = () => {
    api.get(`/trips/${tripId}/expenses`).then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [tripId]); // eslint-disable-line react-hooks/exhaustive-deps

  const terapkanPaket = async () => {
    setApplying(true);
    try {
      const { data: h } = await api.post(`/trips/${tripId}/expenses/paket`);
      if (h.ditambah === 0) toast.info(`Semua kategori paket ${h.komoditas} sudah ada di trip ini (${h.dilewati} dilewati)`);
      else toast.success(`${h.ditambah} kategori dari paket ${h.komoditas} ditambahkan (${fmtRp(h.total_rp)})${h.dilewati ? `, ${h.dilewati} dilewati` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/trips/${tripId}/expenses`, { ...form, Nominal_Rp: parseFloat(form.Nominal_Rp) });
      toast.success("Pengeluaran dicatat");
      setForm(emptyExpense());
      load();
    } catch (err) {
      if (isNetworkError(err)) {
        await queueRequest({
          method: "post",
          url: `/trips/${tripId}/expenses`,
          payload: { ...form, Nominal_Rp: parseFloat(form.Nominal_Rp) },
          label: `Pengeluaran ${form.Kategori_Biaya || "-"} Rp ${form.Nominal_Rp || 0}`,
        });
        toast.warning('Koneksi terputus — pengeluaran disimpan sebagai "Tertunda" dan akan terkirim otomatis saat online', { duration: 6000 });
        setForm(emptyExpense());
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const togglePencairan = async (exp) => {
    const next = (exp.Status_Pencairan || "Belum Dicairkan") === "Belum Dicairkan" ? "Sudah Dicairkan" : "Belum Dicairkan";
    try {
      await api.put(`/trips/${tripId}/expenses/${exp.id}/pencairan`, { Status_Pencairan: next });
      toast.success(`Status pencairan diubah menjadi "${next}"`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeExpense = async (exp) => {
    try {
      await api.delete(`/trips/${tripId}/expenses/${exp.id}`);
      toast.success("Pengeluaran dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Card className="border-zinc-200 shadow-sm" data-testid="trip-expenses-card">
      <CardHeader className="pb-4 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold tracking-tight flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-700" /> Pengeluaran Harian (Uang Jalan)
        </CardTitle>
        <div className="flex items-center gap-2">
          {active && komoditas && (
            <Button type="button" size="sm" variant="outline" onClick={terapkanPaket} disabled={applying}
              data-testid="terapkan-paket-button" title="Isi semua kategori biaya standar komoditas ini sekali klik"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-8">
              {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Layers className="w-4 h-4 mr-1.5" />}
              Terapkan Paket {komoditas}
            </Button>
          )}
          <span data-testid="total-uang-jalan" className="text-sm font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1 tabular-nums">
            Total: {fmtRp(data?.Total_Uang_Jalan)}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!active ? (
          <p data-testid="expenses-locked-note" className="text-sm text-zinc-500 bg-zinc-50 border border-dashed border-zinc-300 rounded-md px-4 py-3">
            Pencatatan pengeluaran aktif setelah Status Trip diubah menjadi "Berangkat".
          </p>
        ) : (
          <form onSubmit={addExpense} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <div className="space-y-1.5">
              <Label>Kategori Biaya *</Label>
              <Select value={form.Kategori_Biaya} onValueChange={(v) => setForm({ ...form, Kategori_Biaya: v })} required>
                <SelectTrigger data-testid="select-kategori-biaya" className="bg-white btn-touch"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {KATEGORI.map((k) => <SelectItem key={k} value={k} data-testid={`kategori-option-${k.replace(/[ /]/g, "-")}`}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nominal (Rp) *</Label>
              <Input data-testid="input-nominal" type="number" inputMode="numeric" min="1" step="1" required placeholder="cth: 150000"
                value={form.Nominal_Rp} onChange={(e) => setForm({ ...form, Nominal_Rp: e.target.value })} className="bg-white input-touch num-hc text-zinc-900" />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal Keluar *</Label>
              <Input data-testid="input-tanggal-keluar" type="date" required value={form.Tanggal_Keluar}
                onChange={(e) => setForm({ ...form, Tanggal_Keluar: e.target.value })} className="bg-white input-touch" />
            </div>
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Input data-testid="input-catatan" placeholder="opsional" value={form.Catatan}
                onChange={(e) => setForm({ ...form, Catatan: e.target.value })} className="bg-white" />
            </div>
            <Button data-testid="add-expense-button" type="submit" disabled={saving || !form.Kategori_Biaya}
              className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 btn-touch">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} Catat
            </Button>
          </form>
        )}

        {data === null ? (
          <div className="flex items-center justify-center py-6 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : data.expenses.length === 0 ? (
          <p data-testid="expenses-empty" className="text-sm text-zinc-400 text-center py-4">Belum ada pengeluaran tercatat untuk trip ini.</p>
        ) : (
          <div className="overflow-x-auto border border-zinc-200 rounded-md">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  {["Tanggal", "Kategori", "Nominal", "Catatan", "Pencairan", ""].map((h, i) => (
                    <th key={i} className="text-left px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-500 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.expenses.map((exp) => (
                  <tr key={exp.id} data-testid={`expense-row-${exp.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-200">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(exp.Tanggal_Keluar)}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{exp.Kategori_Biaya}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap num-hc text-zinc-900">{fmtRp(exp.Nominal_Rp)}</td>
                    <td className="px-4 py-2.5 text-zinc-500">{exp.Catatan || "-"}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <button data-testid={`pencairan-toggle-${exp.id}`} type="button" onClick={() => togglePencairan(exp)}
                        className="icon-touch" title="Klik untuk ubah status pencairan">
                        <Badge className={`cursor-pointer font-medium ${(exp.Status_Pencairan || "Belum Dicairkan") === "Sudah Dicairkan"
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-200"
                          : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"}`}>
                          <CircleDollarSign className="w-3 h-3 mr-1" />
                          {exp.Status_Pencairan || "Belum Dicairkan"}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <button data-testid={`delete-expense-${exp.id}`} type="button" onClick={() => removeExpense(exp)}
                        className="p-1.5 rounded-md text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors duration-200 icon-touch">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
